import React, { useEffect, useState } from 'react';
import {
    ArrowRight, Building2, CalendarCheck, CalendarDays, CheckCircle2, CreditCard, DoorClosed, FileText, Fingerprint, Globe, Hash, Instagram,
    Lock, Mail, MapPin, MessageCircle, Percent, Phone, QrCode, ShieldCheck, Ticket, User, Users, X, Zap,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

import {
    IMPLEMENTATION_FEE, MONTHLY_FEE, EXTRA_USER_FEE, INCLUDED_USERS, brl, type CouponPricing,
} from '../utils/couponPricing';
import { computePlan, CONTRACT_MONTHS, INSTALLMENT_DISCOUNT, type BillingMode } from '../utils/plan';

interface AppliedCoupon {
    code: string;
    description: string | null;
    pricing: CouponPricing;
}

const DEFAULT_PRICING: CouponPricing = { implementation: IMPLEMENTATION_FEE, monthly: MONTHLY_FEE, freeMonths: 0 };

// Textos legais: páginas internas (src/legal/legalContent.ts). Pode apontar para URLs externas se preferir.
const TERMS_URL = '/termos';
const PRIVACY_URL = '/privacidade';

const INSTALLMENTS_LABEL = '12 parcelas no cartão';
const onlyDigits = (s: string) => s.replace(/\D/g, '');

function maskCpfCnpj(value: string): string {
    const d = onlyDigits(value).slice(0, 14);
    if (d.length <= 11) {
        return d
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return d
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
}

function maskPhone(value: string): string {
    const d = onlyDigits(value).slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function maskCard(value: string): string {
    return onlyDigits(value).slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');
}

function maskExpiry(value: string): string {
    const d = onlyDigits(value).slice(0, 4);
    return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
}

function maskCep(value: string): string {
    const d = onlyDigits(value).slice(0, 8);
    return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
}

function isValidCardNumber(number: string): boolean {
    if (number.length < 13 || number.length > 19) return false;
    let sum = 0;
    let alt = false;
    for (let i = number.length - 1; i >= 0; i--) {
        let n = Number(number[i]);
        if (alt) { n *= 2; if (n > 9) n -= 9; }
        sum += n;
        alt = !alt;
    }
    return sum % 10 === 0;
}

function isValidExpiry(value: string): boolean {
    const d = onlyDigits(value);
    if (d.length !== 4) return false;
    const month = Number(d.slice(0, 2));
    const year = 2000 + Number(d.slice(2));
    if (month < 1 || month > 12) return false;
    const now = new Date();
    return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
}

function isValidCpf(cpf: string): boolean {
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
    for (const len of [9, 10]) {
        let sum = 0;
        for (let i = 0; i < len; i++) sum += Number(cpf[i]) * (len + 1 - i);
        const digit = ((sum * 10) % 11) % 10;
        if (digit !== Number(cpf[len])) return false;
    }
    return true;
}

function isValidCnpj(cnpj: string): boolean {
    if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
    for (const len of [12, 13]) {
        const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        let sum = 0;
        for (let i = 0; i < len; i++) sum += Number(cnpj[i]) * weights[i];
        const digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
        if (digit !== Number(cnpj[len])) return false;
    }
    return true;
}

const inputClass =
    'w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 text-base focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all hover:bg-slate-100/70 dark:hover:bg-slate-900 focus:bg-white dark:focus:bg-slate-900 shadow-sm';

interface FieldProps {
    id: string;
    label: string;
    icon: React.ElementType;
    children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ id, label, icon: Icon, children }) => (
    <div className="space-y-2">
        <label htmlFor={id} className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
            {label}
        </label>
        <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                <Icon size={22} />
            </div>
            {children}
        </div>
    </div>
);

const features = [
    { icon: Users, title: 'Gestão de Clientes & Negociações', description: 'Funil de vendas completo para acompanhar todos os seus clientes' },
    { icon: DoorClosed, title: 'Orçamentos de Portas em ACM', description: 'Modelos, medidas de vão, cores de chapa e acabamentos' },
    { icon: MessageCircle, title: 'Atendimento Integrado no WhatsApp', description: 'Centralize conversas e acelere o fechamento' },
    { icon: FileText, title: 'Propostas & Acompanhamento de Pedidos', description: 'Propostas em PDF e cada pedido acompanhado até a entrega' },
];

const steps = [
    { title: 'Preencha seus dados', description: 'Leva menos de um minuto.' },
    { title: 'Escolha como pagar', description: 'Pix ou cartão (até 12x com 5% de desconto), em ambiente seguro do Asaas.' },
    { title: 'Acesso liberado', description: 'Assim que o pagamento é confirmado, sua conta é criada automaticamente.' },
];

type PaymentMethod = BillingMode;
const isCardMethod = (m: PaymentMethod) => m !== 'pix';

// Página pública de contratação: plano de 12 meses, implantação opcional, Pix mensal, cartão recorrente ou cartão em 12x.
// Coleta os dados, gera a cobrança no Asaas e leva o cliente ao pagamento. Empresa e login são criados sozinhos após a confirmação.
export function SignupPage() {
    const [form, setForm] = useState({ company_name: '', manager_name: '', email: '', phone: '', cpf_cnpj: '', website: '' });
    const [method, setMethod] = useState<PaymentMethod>('card_12x');
    const [includeImpl, setIncludeImpl] = useState(true);
    const [card, setCard] = useState({ holderName: '', number: '', expiry: '', ccv: '', postal_code: '', address_number: '' });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState<{ provisioned: boolean; email: string; free: boolean; mode: PaymentMethod } | null>(null);
    const [couponOpen, setCouponOpen] = useState(false);
    const [couponInput, setCouponInput] = useState('');
    const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponError, setCouponError] = useState('');

    // O servidor calcula e cobra de verdade; aqui só mostramos o resultado da prévia
    const pricing = coupon?.pricing ?? DEFAULT_PRICING;
    const discounted = pricing.implementation !== IMPLEMENTATION_FEE || pricing.monthly !== MONTHLY_FEE || pricing.freeMonths > 0;
    const planFor = (mode: PaymentMethod) => computePlan({
        mode, includeImplementation: includeImpl, implementation: pricing.implementation, monthly: pricing.monthly, freeMonths: pricing.freeMonths,
    });
    const plan = planFor(method);
    const plan12x = planFor('card_12x');
    // Mês (1 = o do cadastro) em que a Mensalidade da assinatura é cobrada pela primeira vez
    const firstSubscriptionMonth = plan.subscriptionOffsetMonths + 1;
    const contractTotal = plan.mode === 'card_12x'
        ? plan.chargeNow
        : plan.chargeNow + pricing.monthly * (CONTRACT_MONTHS - (plan.paysMonthlyAtSignup ? 1 : 0));

    const applyCoupon = async (raw: string) => {
        const code = raw.trim().toUpperCase();
        if (!code) { setCouponError('Digite o código do cupom.'); return; }
        if (!supabase) { setCouponError('Serviço indisponível no momento.'); return; }
        setCouponLoading(true);
        setCouponError('');
        try {
            const { data, error: fnError } = await supabase.functions.invoke('asaas-signup', { body: { action: 'validate_coupon', coupon_code: code } });
            let result = data as { valid?: boolean; error?: string; code?: string; description?: string | null; pricing?: CouponPricing } | null;
            if (fnError && !result?.error) {
                try { result = await (fnError as { context?: Response }).context?.json(); } catch { /* sem corpo */ }
            }
            if (!result?.valid || !result.pricing) {
                setCoupon(null);
                setCouponError(result?.error || 'Não foi possível validar o cupom agora.');
                return;
            }
            setCoupon({ code: result.code ?? code, description: result.description ?? null, pricing: result.pricing });
            setCouponInput('');
        } finally {
            setCouponLoading(false);
        }
    };

    // Nicho definido pelo link: /assinar?nicho=cursos (o servidor valida; valor desconhecido vira padrão)
    const nicheFromLink = () => {
        const niche = new URLSearchParams(window.location.search).get('nicho');
        return niche ? { niche } : {};
    };

    // Link com o cupom já aplicado: /assinar?cupom=CODIGO
    useEffect(() => {
        const fromUrl = new URLSearchParams(window.location.search).get('cupom');
        if (fromUrl) {
            setCouponOpen(true);
            void applyCoupon(fromUrl);
        }
        // roda uma vez ao abrir a página
    }, []);

    const setCardField = (key: keyof typeof card, transform?: (v: string) => string) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setCard(prev => ({ ...prev, [key]: transform ? transform(e.target.value) : e.target.value }));

    const set = (key: keyof typeof form, transform?: (v: string) => string) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [key]: transform ? transform(e.target.value) : e.target.value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const doc = onlyDigits(form.cpf_cnpj);
        if (!(doc.length === 11 ? isValidCpf(doc) : isValidCnpj(doc))) {
            setError('CPF ou CNPJ inválido. Confira os números.');
            return;
        }
        const phone = onlyDigits(form.phone);
        if (phone && (phone.length < 10 || phone.length > 11)) {
            setError('Telefone inválido. Informe DDD + número.');
            return;
        }
        if (!acceptTerms) {
            setError('Para continuar, aceite os Termos e a Política de Privacidade.');
            return;
        }
        if (isCardMethod(method)) {
            if (card.holderName.trim().length < 3) { setError('Informe o nome impresso no cartão.'); return; }
            if (!isValidCardNumber(onlyDigits(card.number))) { setError('Número do cartão inválido.'); return; }
            if (!isValidExpiry(card.expiry)) { setError('Validade do cartão inválida ou vencida.'); return; }
            if (onlyDigits(card.ccv).length < 3) { setError('Código de segurança inválido.'); return; }
            if (onlyDigits(card.postal_code).length !== 8) { setError('CEP inválido.'); return; }
            if (!card.address_number.trim()) { setError('Informe o número do endereço.'); return; }
            if (phone.length < 10) { setError('Informe o WhatsApp com DDD para a cobrança no cartão.'); return; }
        }
        if (!supabase) {
            setError('Serviço indisponível no momento. Tente novamente em instantes.');
            return;
        }

        setLoading(true);
        try {
            const common = { accept_terms: true, payment_method: method, include_implementation: includeImpl };
            const payload = isCardMethod(method)
                ? {
                    ...form,
                    ...common,
                    card: {
                        holderName: card.holderName.trim(),
                        number: onlyDigits(card.number),
                        expiryMonth: onlyDigits(card.expiry).slice(0, 2),
                        expiryYear: onlyDigits(card.expiry).slice(2),
                        ccv: onlyDigits(card.ccv),
                    },
                    postal_code: onlyDigits(card.postal_code),
                    address_number: card.address_number.trim(),
                    ...(coupon ? { coupon_code: coupon.code } : {}),
                    ...nicheFromLink(),
                }
                : { ...form, ...common, ...(coupon ? { coupon_code: coupon.code } : {}), ...nicheFromLink() };
            const { data, error: fnError } = await supabase.functions.invoke('asaas-signup', { body: payload });
            // Respostas 4xx trazem a mensagem no corpo
            let message = data?.error as string | undefined;
            if (fnError && !message) {
                try { message = (await (fnError as { context?: Response }).context?.json())?.error; } catch { /* sem corpo */ }
            }
            if (message) throw new Error(message);
            if (isCardMethod(method) || data?.free) {
                if (!data?.approved) throw new Error('Não foi possível concluir o cadastro.');
                setCard({ holderName: '', number: '', expiry: '', ccv: '', postal_code: '', address_number: '' });
                setDone({ provisioned: !!data.provisioned, email: form.email, free: !!data.free, mode: method });
                setLoading(false);
                return;
            }
            if (!data?.invoiceUrl) throw new Error('Não foi possível gerar a cobrança.');
            window.location.href = data.invoiceUrl;
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Erro inesperado. Tente novamente.');
            setLoading(false);
        }
    };

    const free = plan.chargeNow === 0;
    const buttonLabel = method === 'card_12x'
        ? `Pagar em ${plan.installmentCount}x de ${brl(plan.installmentValue ?? 0)}`
        : method === 'card_auto'
            ? (free ? 'Cadastrar cartão e criar conta' : `Pagar ${brl(plan.chargeNow)} no cartão`)
            : (free ? 'Criar minha conta' : `Pagar ${brl(plan.chargeNow)} por Pix`);

    return (
        <div className="min-h-screen bg-slate-950 flex font-sans">
            {/* Painel da marca */}
            <div className="hidden lg:flex lg:w-1/2 xl:w-5/12 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden flex-col justify-between p-12 xl:p-16 border-r border-slate-800/60 isolate">
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
                    <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-yellow-600/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
                </div>

                <div className="relative">
                    <div className="flex items-center gap-3 mb-12">
                        <img src="/logo-orion.svg" alt="Orion CRM" className="w-11 h-11 object-contain drop-shadow-[0_0_12px_rgba(196,154,69,0.3)]" />
                        <div>
                            <h1 className="text-3xl font-extrabold text-white tracking-tight leading-none">Orion CRM</h1>
                            <span className="text-xs font-semibold text-blue-400 tracking-wide">Método ACM • Evolua Negócios</span>
                        </div>
                    </div>

                    <div className="space-y-5 mb-10">
                        <h2 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight tracking-tight">
                            Tenha seu CRM <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-yellow-200 to-blue-300">
                                pronto para vender.
                            </span>
                        </h2>
                        <p className="text-slate-300 text-base xl:text-lg leading-relaxed max-w-lg">
                            Contrate online, pague e comece a usar. Sua empresa e seu acesso de gestor são criados automaticamente, sem espera.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {features.map((feature) => (
                            <div
                                key={feature.title}
                                className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/40 border border-slate-800/60 backdrop-blur-sm transition-all hover:bg-slate-800/40 hover:border-slate-700/60"
                            >
                                <div className="w-11 h-11 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 mt-0.5">
                                    <feature.icon className="w-5 h-5 text-blue-400" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-white mb-1">{feature.title}</h3>
                                    <p className="text-sm text-slate-500 leading-relaxed">{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative pt-8 border-t border-slate-800/60 mt-8 flex items-center justify-between text-xs text-slate-500">
                    <p>© 2026 Orion CRM • Em parceria com Evolua Negócios.</p>
                    <div className="flex items-center gap-3">
                        <a href="https://evoluanegocios.com" target="_blank" rel="noopener noreferrer" title="Site Oficial" aria-label="Site oficial"
                            className="inline-flex items-center justify-center w-[44px] h-[44px] rounded-lg bg-slate-800/60 hover:bg-slate-700 hover:text-white transition-colors">
                            <Globe size={14} />
                        </a>
                        <a href="https://instagram.com/evoluanegocios" target="_blank" rel="noopener noreferrer" title="Instagram" aria-label="Instagram"
                            className="inline-flex items-center justify-center w-[44px] h-[44px] rounded-lg bg-slate-800/60 hover:bg-slate-700 hover:text-white transition-colors">
                            <Instagram size={14} />
                        </a>
                    </div>
                </div>
            </div>

            {/* Formulário */}
            <div className="w-full lg:w-1/2 xl:w-7/12 flex items-start lg:items-center justify-center p-6 sm:p-12 bg-slate-50 dark:bg-slate-950 relative transition-colors isolate overflow-hidden">
                <div className="absolute inset-0 pointer-events-none -z-10">
                    <div className="absolute -top-20 -right-20 w-[600px] h-[600px] bg-blue-500/10 dark:bg-blue-600/10 rounded-full blur-[120px]"></div>
                    <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-yellow-500/10 dark:bg-yellow-600/10 rounded-full blur-[100px]"></div>
                </div>

                <div className="w-full max-w-[620px] flex flex-col py-6">
                    <div className="flex justify-center mb-8">
                        <img src="/orion-acm-light.svg" alt="Orion CRM - Evolua Negócios" className="w-[300px] sm:w-[400px] h-auto object-contain dark:hidden"
                            onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                        <img src="/orion-acm-dark.svg" alt="Orion CRM - Evolua Negócios" className="w-[300px] sm:w-[400px] h-auto object-contain hidden dark:block"
                            onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                    </div>

                    <div className="w-full bg-white/70 dark:bg-slate-900/40 backdrop-blur-2xl border border-white/50 dark:border-slate-700/50 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] rounded-[2.5rem] p-8 sm:p-12">
                        {done ? (
                            <div className="text-center py-4" role="status">
                                <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                                </div>
                                <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{done.free ? 'Conta criada!' : 'Pagamento aprovado!'}</h2>
                                {done.provisioned ? (
                                    <>
                                        <p className="text-base text-slate-500 dark:text-slate-400 mt-3">
                                            Sua conta foi criada. Entre no sistema com os dados abaixo.
                                        </p>
                                        <dl className="mt-6 text-left rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-slate-50/70 dark:bg-slate-900/50 p-5 space-y-3 text-sm">
                                            <div className="flex justify-between gap-4"><dt className="text-slate-500">Login</dt><dd className="font-semibold text-slate-900 dark:text-white break-all text-right">{done.email}</dd></div>
                                            <div className="flex justify-between gap-4"><dt className="text-slate-500">Senha inicial</dt><dd className="font-semibold text-slate-900 dark:text-white">Evolua123</dd></div>
                                        </dl>
                                        <a href="/" className="mt-6 w-full min-h-[44px] py-4 px-6 bg-yellow-600 hover:bg-yellow-500 text-slate-950 rounded-xl text-base font-bold shadow-[0_4px_14px_0_rgba(202,138,4,0.39)] transition-all flex items-center justify-center gap-2">
                                            <span>Acessar o sistema</span>
                                            <ArrowRight size={18} />
                                        </a>
                                        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                                            {done.mode === 'card_12x'
                                                ? `Seu plano de ${CONTRACT_MONTHS} meses está pago em ${INSTALLMENTS_LABEL}. Depois dele, o plano segue mensal por Pix até você cancelar.`
                                                : done.mode === 'card_auto'
                                                    ? `Plano de ${CONTRACT_MONTHS} meses. A mensalidade de ${brl(pricing.monthly)} será cobrada automaticamente neste cartão a partir do ${firstSubscriptionMonth}º mês.`
                                                    : `Plano de ${CONTRACT_MONTHS} meses. A mensalidade de ${brl(pricing.monthly)} é cobrada por Pix a partir do ${firstSubscriptionMonth}º mês; o Asaas envia o Pix ao e-mail informado.`}
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-base text-slate-500 dark:text-slate-400 mt-3">
                                        Recebemos o pagamento e estamos finalizando a criação do seu acesso. Em alguns minutos você poderá entrar com o e-mail <strong className="text-slate-700 dark:text-slate-200">{done.email}</strong> e a senha inicial <strong className="text-slate-700 dark:text-slate-200">Evolua123</strong>. Se demorar, fale com a Evolua Negócios.
                                    </p>
                                )}
                            </div>
                        ) : (
                        <>
                        <div className="mb-8 text-center">
                            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">Contrate seu CRM</h2>
                            <p className="text-base text-slate-500 dark:text-slate-400 mt-2">
                                Plano de 12 meses. Informe os dados da sua empresa e escolha como pagar.
                            </p>
                        </div>

                        {/* Aviso do plano anual */}
                        <div className="mb-4 flex items-center justify-center gap-2 rounded-xl border border-yellow-600/40 bg-yellow-500/10 px-4 py-3" role="note">
                            <CalendarCheck className="w-5 h-5 shrink-0 text-yellow-700 dark:text-yellow-500" />
                            <p className="text-sm font-bold text-slate-900 dark:text-white">Plano anual <span className="font-normal text-slate-600 dark:text-slate-300">· {CONTRACT_MONTHS} meses</span></p>
                        </div>

                        {/* Resumo do investimento */}
                        <div className="mb-6 rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-gradient-to-br from-yellow-500/5 via-transparent to-blue-500/5 p-5">
                            <p className="text-xs font-bold uppercase tracking-wider text-yellow-700 dark:text-yellow-500">Seu plano</p>
                            <dl className="mt-3 space-y-3 text-sm">
                                <div className="flex items-start justify-between gap-4">
                                    <dt className="text-slate-600 dark:text-slate-300">
                                        Mensalidade
                                        <span className="block text-xs text-slate-500">
                                            {pricing.freeMonths > 0 && <><strong>{pricing.freeMonths} {pricing.freeMonths === 1 ? 'mês grátis' : 'meses grátis'}</strong> · </>}
                                            {CONTRACT_MONTHS} meses · {INCLUDED_USERS} usuários inclusos (1 gestor e 1 vendedor)
                                        </span>
                                    </dt>
                                    <dd className="text-right font-bold text-slate-900 dark:text-white whitespace-nowrap">
                                        {pricing.monthly !== MONTHLY_FEE && <span className="text-xs font-normal text-slate-400 line-through mr-1">{brl(MONTHLY_FEE)}</span>}
                                        {brl(pricing.monthly)}<span className="text-xs font-normal text-slate-500">/mês</span>
                                    </dd>
                                </div>
                                <div className="flex items-start justify-between gap-4">
                                    <dt className="text-slate-600 dark:text-slate-300">
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input type="checkbox" checked={includeImpl} onChange={(e) => setIncludeImpl(e.target.checked)}
                                                className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 accent-yellow-600" />
                                            <span>
                                                Incluir implantação <span className="text-xs text-slate-500">(opcional, pagamento único)</span>
                                                <span className="block text-xs text-slate-500">
                                                    {includeImpl ? 'O 1º mês é coberto pela implantação; a mensalidade começa no 2º mês.' : 'Sem implantação, a 1ª mensalidade é cobrada hoje.'}
                                                </span>
                                            </span>
                                        </label>
                                    </dt>
                                    <dd className="text-right font-bold text-slate-900 dark:text-white whitespace-nowrap">
                                        {includeImpl ? (
                                            <>
                                                {pricing.implementation !== IMPLEMENTATION_FEE && <span className="text-xs font-normal text-slate-400 line-through mr-1">{brl(IMPLEMENTATION_FEE)}</span>}
                                                {pricing.implementation === 0 ? 'Grátis' : brl(pricing.implementation)}
                                            </>
                                        ) : <span className="text-slate-400 font-normal">—</span>}
                                    </dd>
                                </div>
                                {plan.mode === 'card_12x' && plan.installmentDiscount > 0 && (
                                    <div className="flex items-start justify-between gap-4 text-emerald-700 dark:text-emerald-400">
                                        <dt className="flex items-center gap-1.5"><Percent size={14} /> Desconto de {INSTALLMENT_DISCOUNT * 100}% no cartão em 12x</dt>
                                        <dd className="font-bold whitespace-nowrap">− {brl(plan.installmentDiscount)}</dd>
                                    </div>
                                )}
                            </dl>
                            <div className="mt-4 pt-4 border-t border-slate-200/70 dark:border-slate-700/60 flex items-end justify-between gap-4">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-yellow-700 dark:text-yellow-500">Pagamento de hoje</p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {plan.mode === 'card_12x'
                                            ? `Parcelas no cartão · total de ${brl(plan.chargeNow)}`
                                            : free ? 'Nada a pagar agora'
                                                : plan.paysMonthlyAtSignup ? '1ª mensalidade' : 'Implantação (pagamento único)'}
                                    </p>
                                </div>
                                {plan.mode === 'card_12x' ? (
                                    <p className="text-3xl font-extrabold text-slate-900 dark:text-white text-right">
                                        <span className="text-base font-bold">{plan.installmentCount}x </span>{brl(plan.installmentValue ?? 0)}
                                    </p>
                                ) : (
                                    <p className="text-3xl font-extrabold text-slate-900 dark:text-white">{free ? 'Grátis' : brl(plan.chargeNow)}</p>
                                )}
                            </div>
                            <ul className="mt-4 pt-4 border-t border-slate-200/70 dark:border-slate-700/60 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
                                    <span>
                                        {plan.mode === 'card_12x'
                                            ? <>Total de <strong>{brl(contractTotal)}</strong> pelos {CONTRACT_MONTHS} meses. Depois, renovação mensal de {brl(pricing.monthly)} por Pix.</>
                                            : <>Total de <strong>{brl(contractTotal)}</strong> nos {CONTRACT_MONTHS} meses. A mensalidade de {brl(pricing.monthly)} {plan.mode === 'card_auto' ? 'sai sozinha no cartão' : 'chega por Pix'} a partir do {firstSubscriptionMonth}º mês.</>}
                                    </span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
                                    <span>Precisa de mais acessos? Adicione dentro do CRM por {brl(EXTRA_USER_FEE)}/mês cada</span>
                                </li>
                            </ul>
                        </div>

                        {/* Cupom */}
                        <div className="mb-8">
                            {coupon ? (
                                <div className="flex items-start justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                                    <div className="flex items-start gap-3">
                                        <Ticket className="w-5 h-5 mt-0.5 text-emerald-500 shrink-0" />
                                        <div>
                                            <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Cupom <span className="font-mono tracking-wider">{coupon.code}</span> aplicado</p>
                                            {coupon.description && <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{coupon.description}</p>}
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => { setCoupon(null); setCouponError(''); }} aria-label="Remover cupom"
                                        className="shrink-0 w-[44px] h-[44px] -m-2 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-200">
                                        <X size={18} />
                                    </button>
                                </div>
                            ) : couponOpen ? (
                                <div>
                                    <label htmlFor="coupon_code" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Cupom de desconto</label>
                                    <div className="flex gap-2">
                                        <input id="coupon_code" value={couponInput} autoComplete="off" autoCapitalize="characters" maxLength={32}
                                            onChange={(e) => { setCouponInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '')); setCouponError(''); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void applyCoupon(couponInput); } }}
                                            placeholder="Digite o código" className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 text-slate-900 dark:text-white font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
                                        <button type="button" onClick={() => void applyCoupon(couponInput)} disabled={couponLoading || !couponInput}
                                            className="px-5 py-3 rounded-xl bg-slate-900 dark:bg-slate-700 text-white text-sm font-semibold hover:bg-slate-800 dark:hover:bg-slate-600 disabled:opacity-50">
                                            {couponLoading ? 'Verificando...' : 'Aplicar'}
                                        </button>
                                    </div>
                                    {couponError && <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">{couponError}</p>}
                                </div>
                            ) : (
                                <button type="button" onClick={() => setCouponOpen(true)}
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline min-h-[44px]">
                                    <Ticket size={16} /> Tenho um cupom de desconto
                                </button>
                            )}
                        </div>

                        {error && (
                            <div role="alert" className="mb-6 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 rounded-xl flex items-start gap-3 text-red-700 dark:text-red-300 text-sm">
                                <span className="shrink-0 mt-0.5">⚠️</span>
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <Field id="company_name" label="Nome da empresa" icon={Building2}>
                                <input id="company_name" name="organization" autoComplete="organization" required minLength={2}
                                    value={form.company_name} onChange={set('company_name')} className={inputClass} placeholder="Ex: Portas Silva ACM" />
                            </Field>

                            <Field id="manager_name" label="Seu nome (gestor)" icon={User}>
                                <input id="manager_name" name="name" autoComplete="name" required minLength={2}
                                    value={form.manager_name} onChange={set('manager_name')} className={inputClass} placeholder="Nome completo" />
                            </Field>

                            <Field id="email" label="E-mail (será o seu login)" icon={Mail}>
                                <input id="email" name="email" type="email" autoComplete="email" required
                                    value={form.email} onChange={set('email', v => v.trim())} className={inputClass} placeholder="nome@empresa.com" />
                            </Field>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <Field id="cpf_cnpj" label="CPF ou CNPJ" icon={Fingerprint}>
                                    <input id="cpf_cnpj" name="cpf_cnpj" inputMode="numeric" required
                                        value={form.cpf_cnpj} onChange={set('cpf_cnpj', maskCpfCnpj)} className={inputClass} placeholder="000.000.000-00" />
                                </Field>
                                <Field id="phone" label="WhatsApp" icon={Phone}>
                                    <input id="phone" name="tel" type="tel" inputMode="tel" autoComplete="tel"
                                        value={form.phone} onChange={set('phone', maskPhone)} className={inputClass} placeholder="(00) 00000-0000" />
                                </Field>
                            </div>

                            {/* Forma de pagamento */}
                            <fieldset className="pt-2">
                                <legend className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Como você prefere pagar?</legend>
                                <div className="grid grid-cols-1 gap-3">
                                    {([
                                        { value: 'card_12x', icon: CreditCard, title: 'Cartão em 12x', badge: `${INSTALLMENT_DISCOUNT * 100}% de desconto`,
                                          text: `${plan12x.installmentCount}x de ${brl(plan12x.installmentValue ?? 0)} no cartão (total de ${brl(plan12x.chargeNow)}).` },
                                        { value: 'card_auto', icon: CreditCard, title: 'Cartão recorrente', badge: null,
                                          text: 'A mensalidade é cobrada automaticamente no cartão todo mês, durante os 12 meses do plano.' },
                                        { value: 'pix', icon: QrCode, title: 'Pix mensal', badge: null,
                                          text: 'Todo mês você recebe o Pix da mensalidade para pagar, durante os 12 meses do plano.' },
                                    ] as const).map(opt => (
                                        <label key={opt.value}
                                            className={`cursor-pointer rounded-2xl border p-4 transition-all ${method === opt.value
                                                ? 'border-yellow-600 bg-yellow-500/10 shadow-sm'
                                                : 'border-slate-200 dark:border-slate-700/70 bg-white/50 dark:bg-slate-900/40 hover:border-slate-300 dark:hover:border-slate-600'}`}>
                                            <input type="radio" name="payment_method" value={opt.value} checked={method === opt.value}
                                                onChange={() => { setMethod(opt.value); setError(''); }} className="sr-only" />
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <opt.icon size={18} className={method === opt.value ? 'text-yellow-600' : 'text-slate-400'} />
                                                <span className="text-sm font-bold text-slate-900 dark:text-white">{opt.title}</span>
                                                {opt.badge && <span className="text-[11px] font-bold uppercase tracking-wide rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-0.5">{opt.badge}</span>}
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{opt.text}</p>
                                        </label>
                                    ))}
                                </div>
                            </fieldset>

                            {isCardMethod(method) && (
                                <div className="space-y-5 rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-900/30 p-5">
                                    <Field id="cc_holder" label="Nome impresso no cartão" icon={User}>
                                        <input id="cc_holder" name="cc-name" autoComplete="cc-name" required value={card.holderName}
                                            onChange={setCardField('holderName', v => v.toUpperCase())} className={inputClass} placeholder="COMO ESTÁ NO CARTÃO" />
                                    </Field>
                                    <Field id="cc_number" label="Número do cartão" icon={CreditCard}>
                                        <input id="cc_number" name="cc-number" autoComplete="cc-number" inputMode="numeric" required value={card.number}
                                            onChange={setCardField('number', maskCard)} className={inputClass} placeholder="0000 0000 0000 0000" />
                                    </Field>
                                    <div className="grid grid-cols-2 gap-5">
                                        <Field id="cc_expiry" label="Validade" icon={CalendarDays}>
                                            <input id="cc_expiry" name="cc-exp" autoComplete="cc-exp" inputMode="numeric" required value={card.expiry}
                                                onChange={setCardField('expiry', maskExpiry)} className={inputClass} placeholder="MM/AA" />
                                        </Field>
                                        <Field id="cc_ccv" label="Código (CVV)" icon={Lock}>
                                            <input id="cc_ccv" name="cc-csc" autoComplete="cc-csc" inputMode="numeric" required value={card.ccv}
                                                onChange={setCardField('ccv', v => onlyDigits(v).slice(0, 4))} className={inputClass} placeholder="123" />
                                        </Field>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <Field id="cc_cep" label="CEP do titular" icon={MapPin}>
                                            <input id="cc_cep" name="postal-code" autoComplete="postal-code" inputMode="numeric" required value={card.postal_code}
                                                onChange={setCardField('postal_code', maskCep)} className={inputClass} placeholder="00000-000" />
                                        </Field>
                                        <Field id="cc_number_addr" label="Número do endereço" icon={Hash}>
                                            <input id="cc_number_addr" name="address-number" required value={card.address_number}
                                                onChange={setCardField('address_number', v => v.slice(0, 10))} className={inputClass} placeholder="Ex: 100" />
                                        </Field>
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed flex items-start gap-2">
                                        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-emerald-500" />
                                        <span>
                                            Os dados do cartão são enviados com criptografia (HTTPS) ao Asaas e não ficam guardados no nosso sistema.
                                            {' '}
                                            {method === 'card_12x'
                                                ? <>Cobramos {brl(plan.chargeNow)} agora, parcelado em {plan.installmentCount}x no cartão. Não guardamos o cartão para novas cobranças.</>
                                                : free
                                                    ? <>Nada será cobrado agora. O cartão fica registrado para a mensalidade de {brl(pricing.monthly)}, a partir do {firstSubscriptionMonth}º mês.</>
                                                    : <>Cobramos {brl(plan.chargeNow)} agora e {brl(pricing.monthly)} por mês a partir do {firstSubscriptionMonth}º mês, no mesmo cartão.</>}
                                        </span>
                                    </p>
                                </div>
                            )}

                            {/* Campo isca contra bots */}
                            <input tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={form.website} onChange={set('website')} className="hidden" />

                            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-900/30 p-4">
                                <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} required
                                    className="mt-1 h-5 w-5 shrink-0 rounded border-slate-300 accent-yellow-600" />
                                <span className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                    Li e aceito os{' '}
                                    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">Termos de Contratação</a>
                                    {' '}e a{' '}
                                    <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">Política de Privacidade</a>.
                                    {' '}Estou ciente de que o plano é de <strong>{CONTRACT_MONTHS} meses</strong> em qualquer forma de pagamento e de que cancelar antes do fim não dispensa as mensalidades restantes.
                                    {method === 'card_12x' && (
                                        <>
                                            {' '}Autorizo a cobrança de <strong>{brl(plan.chargeNow)}</strong> neste cartão, parcelada em {plan.installmentCount}x de {brl(plan.installmentValue ?? 0)},
                                            já com 5% de desconto. Depois dos {CONTRACT_MONTHS} meses, o plano passa a ser renovado mensalmente por Pix ({brl(pricing.monthly)}/mês) até eu cancelar.
                                        </>
                                    )}
                                    {method === 'card_auto' && (
                                        <>
                                            {' '}Autorizo a cobrança de{' '}
                                            {free ? 'nada agora e de ' : <><strong>{brl(plan.chargeNow)}</strong> agora e de </>}
                                            <strong>{brl(pricing.monthly)}</strong> por mês
                                            (mais {brl(EXTRA_USER_FEE)} por usuário adicional que eu contratar) neste cartão, de forma recorrente, a partir do {firstSubscriptionMonth}º mês, durante o plano e, depois dele, até eu cancelar.
                                        </>
                                    )}
                                    {method === 'pix' && (
                                        <>
                                            {' '}Estou ciente de que receberei um Pix de <strong>{brl(pricing.monthly)}</strong> por mês (mais {brl(EXTRA_USER_FEE)} por usuário adicional que eu contratar), a partir do {firstSubscriptionMonth}º mês,
                                            e de que depois dos {CONTRACT_MONTHS} meses o plano segue mensal até eu cancelar.
                                        </>
                                    )}
                                    {discounted && (
                                        <>
                                            {' '}Condições promocionais do cupom{coupon ? ` ${coupon.code}` : ''}: valem durante o período informado; depois, aplicam-se os valores regulares dos Termos.
                                        </>
                                    )}
                                </span>
                            </label>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full min-h-[44px] py-4 px-6 bg-yellow-600 hover:bg-yellow-500 text-slate-950 rounded-xl text-base font-bold shadow-[0_4px_14px_0_rgba(202,138,4,0.39)] hover:shadow-[0_6px_20px_rgba(202,138,4,0.23)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin"></div>
                                        <span>{isCardMethod(method) ? 'Processando...' : free ? 'Criando sua conta...' : 'Gerando pagamento seguro...'}</span>
                                    </>
                                ) : (
                                    <>
                                        <span>{buttonLabel}</span>
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-500 dark:text-slate-400">
                            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-500" /> Pagamento processado pelo Asaas</span>
                            <span className="inline-flex items-center gap-1.5"><CreditCard size={14} className="text-blue-500" /> Pix ou cartão</span>
                            <span className="inline-flex items-center gap-1.5"><Lock size={14} className="text-slate-400" /> Seus dados protegidos</span>
                        </div>
                        </>
                        )}
                    </div>

                    {/* Como funciona */}
                    <ol className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {steps.map((step, i) => (
                            <li key={step.title} className="rounded-2xl bg-white/60 dark:bg-slate-900/40 border border-slate-200/70 dark:border-slate-800/70 p-4">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-sm font-bold mb-2">{i + 1}</span>
                                <p className="text-sm font-bold text-slate-900 dark:text-white">{step.title}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{step.description}</p>
                            </li>
                        ))}
                    </ol>

                    <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
                        <Zap size={14} className="text-yellow-600 inline -mt-0.5 mr-1.5" />
                        Após a confirmação, entre com o e-mail informado e a senha inicial <strong className="text-slate-700 dark:text-slate-200">Evolua123</strong>.
                    </p>
                    <p className="mt-3 text-center text-sm text-slate-500 dark:text-slate-400">
                        Já é cliente? <a href="/" className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">Acessar minha conta</a>
                    </p>
                </div>
            </div>
        </div>
    );
}
